'use client';
import React, {
    MutableRefObject,
    PointerEvent,
    TouchEvent,
    useEffect,
    useRef,
    useState,
} from 'react';
import Typography from '@/components/shared/typography/typography.component';
import { useTranslation } from '@/hook';
import useDebounce from '@/hook/debounce.hook';
import { slugify } from '@/utils/filterUtil';

export interface TFilterGroupRangeProps {
    data: {
        min: number;
        max: number;
    };
    range: number[];
    filterType: 'slider';
    rangeChanged: (range: number[]) => void;
    label: string;
    vertical: boolean;
    formatDisplayedValue: (value: number) => string;
}
enum SLIDER_CONTROLS {
    VERTICAL1 = 'VERTICAL1',
    VERTICAL2 = 'VERTICAL2',
    HORIZONTAL1 = 'HORIZONTAL1',
    HORIZONTAL2 = 'HORIZONTAL2',
}
const FilterGroupSlider = ({
    data,
    label,
    rangeChanged,
    vertical = false,
    range,
    formatDisplayedValue,
}: TFilterGroupRangeProps) => {
    const { IS_RTL } = useTranslation();

    const getRoundingIncrement = () => {
        const range = data.max - data.min;
        return range < 1000 ? 1 : 10 ** (Math.floor(Math.log10(range)) - 2);
    };

    const getDisplayMin = () => {
        const roundingTo = getRoundingIncrement();
        return Math.floor(data.min / roundingTo) * roundingTo;
    };

    const getDisplayMax = () => {
        const roundingTo = getRoundingIncrement();
        return Math.ceil(data.max / roundingTo) * roundingTo;
    };

    const getValRelevant = (val: number) => {
        const displayMin = getDisplayMin();
        const displayMax = getDisplayMax();
        return (
            Math.round(
                ((val - displayMin) / (displayMax - displayMin)) * 10000
            ) / 100
        );
    };
    const getAbsDiff = (val1: number, val2: number) =>
        Math.round(Math.abs(val1 - val2));

    const getAbsVal = (val: number) => {
        const displayMin = getDisplayMin();
        const displayMax = getDisplayMax();
        const rawValue = ((displayMax - displayMin) * val) / 100 + displayMin;
        const roundingTo = getRoundingIncrement();

        return Math.round(rawValue / roundingTo) * roundingTo;
    };

    const [minPosition, setMinPosition] = useState(getValRelevant(range[0]));
    const [positionTop, setPositionTop] = useState(getValRelevant(range[0]));

    const [maxPosition, setMaxPosition] = useState(getValRelevant(range[1]));
    const [positionBottom, setPositionBottom] = useState(
        getValRelevant(range[1])
    );

    const updateMinArm = (val: number) => {
        setMinPosition(val);
        setPositionTop(val);
    };
    const updateMaxArm = (val: number) => {
        setMaxPosition(val);
        setPositionBottom(val);
    };

    const [markerSize, setMarkerSize] = useState(
        getAbsDiff(getValRelevant(range[0]), getValRelevant(range[1]))
    );

    const rightHand = useRef<HTMLDivElement>(null);
    const leftHand = useRef<HTMLDivElement>(null);
    const trackerRef = useRef<HTMLDivElement>(null);
    const trackerVerticalRef = useRef<HTMLDivElement>(null);

    const edgeInControl = useRef(null);

    const clientX = useRef(null);
    const clientY = useRef(null);

    const lastRight = useRef<number>(null);
    const lastLeft = useRef<number | null>(null);

    const lastTop = useRef<number | null>(null);
    const lastBottom = useRef<number | null>(null);
    const updateChanges = useDebounce((val1: number, val2: number) => {
        const vals = [getAbsVal(val1), getAbsVal(val2)].sort((a, b) => a - b);
        rangeChanged(vals);
    }, 300);

    useEffect(() => {
        if (!rightHand.current || !leftHand.current) return;

        document.addEventListener('pointermove', handleTouchMove, {
            passive: false,
            capture: true,
        });
        document.addEventListener('touchmove', handleTouchMove, {
            passive: false,
            capture: true,
        });
        document.addEventListener('pointerup', handleTouchEnd);
        document.addEventListener('touchend', handleTouchEnd, {
            passive: false,
            capture: true,
        });
        return () => {
            document.removeEventListener('pointermove', handleTouchMove);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('pointerup', handleTouchEnd);
            document.removeEventListener('touchend', handleTouchEnd, {
                capture: true,
            });
        };
    }, [rightHand.current]);

    //
    function isTouchEvent(
        e: React.SyntheticEvent | Event
    ): e is React.TouchEvent {
        return e && 'touches' in e;
    }

    function isMouseEvent(
        e: React.SyntheticEvent | Event
    ): e is React.MouseEvent {
        return e && 'clientX' in e;
    }

    interface IFirstTouch {
        clientX: number;
        clientY: number;
    }
    const getFirstTouch = (e: React.SyntheticEvent | Event): IFirstTouch => {
        if (isTouchEvent(e)) {
            return {
                clientX: e.touches[0].clientX,
                clientY: e.touches[0].clientY,
            };
        } else if (isMouseEvent(e)) {
            return {
                clientX: e.clientX,
                clientY: e.clientY,
            };
        }
        return null;
    };
    const handleHorizontalBarClicked = (
        clickedPoint: IFirstTouch,
        tracker: MutableRefObject<HTMLDivElement>
    ) => {
        const trackerPosition = tracker.current.getBoundingClientRect();

        const innerClickedPoint = clickedPoint.clientX - trackerPosition.left;
        const relativePosition =
            (innerClickedPoint / trackerPosition.width) * 100;

        const newPosition = IS_RTL
            ? 100 - Math.round(Math.max(0, Math.min(100, relativePosition)))
            : Math.round(Math.max(0, Math.min(100, relativePosition)));

        let movingArm = SLIDER_CONTROLS.HORIZONTAL1;
        //decide which arm will move.
        if (
            getAbsDiff(minPosition, newPosition) <
            getAbsDiff(maxPosition, newPosition)
        ) {
            movingArm = SLIDER_CONTROLS.HORIZONTAL2;
        }

        if (movingArm === SLIDER_CONTROLS.HORIZONTAL2) {
            updateMinArm(newPosition);
            setMarkerSize(getAbsDiff(newPosition, maxPosition));
            updateChanges(newPosition, maxPosition);
        } else {
            updateMaxArm(newPosition);
            setMarkerSize(getAbsDiff(newPosition, minPosition));
            updateChanges(newPosition, minPosition);
        }
    };
    const handleVerticalBarClicked = (
        clickedPoint: IFirstTouch,
        tracker: MutableRefObject<HTMLDivElement>
    ): void => {
        const trackerPosition = tracker.current.getBoundingClientRect();

        const innerClickedPoint = clickedPoint.clientY - trackerPosition.top;
        const relativePosition =
            (innerClickedPoint / trackerPosition.height) * 100;

        const newPosition =
            100 - Math.round(Math.max(0, Math.min(100, relativePosition)));
        let movingArm = SLIDER_CONTROLS.VERTICAL2;
        //decide which arm will move.
        if (
            getAbsDiff(positionBottom, newPosition) <
            getAbsDiff(positionTop, newPosition)
        ) {
            movingArm = SLIDER_CONTROLS.VERTICAL1;
        }

        if (movingArm === SLIDER_CONTROLS.VERTICAL2) {
            updateMinArm(newPosition);
            setMarkerSize(getAbsDiff(newPosition, positionBottom));
            updateChanges(newPosition, positionBottom);
        } else {
            updateMaxArm(newPosition);
            setMarkerSize(getAbsDiff(newPosition, positionTop));
            updateChanges(newPosition, positionTop);
        }
    };
    const handleBarClicked = (e: PointerEvent | TouchEvent) => {
        e.preventDefault();
        const clickedPoint = getFirstTouch(e);
        if (!clickedPoint) return;
        if (vertical) {
            handleVerticalBarClicked(clickedPoint, trackerVerticalRef);
        } else {
            handleHorizontalBarClicked(clickedPoint, trackerRef);
        }
    };

    const handleTouchStart = (
        evt: PointerEvent | TouchEvent | React.FocusEvent<HTMLButtonElement>,
        inControl: string
    ) => {
        evt.stopPropagation();
        const touch = getFirstTouch(evt);
        edgeInControl.current = inControl;
        if (IS_RTL) {
            lastRight.current = minPosition;
            lastLeft.current = maxPosition;
        } else {
            lastRight.current = maxPosition;
            lastLeft.current = minPosition;
        }
        lastTop.current = positionTop;
        lastBottom.current = positionBottom;

        clientX.current = touch.clientX;
        clientY.current = touch.clientY;
    };

    const handleTouchMove = (e: Event) => {
        const touch = getFirstTouch(e);
        if (
            !touch ||
            !edgeInControl.current ||
            (!trackerVerticalRef.current && !trackerRef.current) // in re-render process and trackers still no avb.
        )
            return;
        // after eval the prev condition.
        // then this handler is the only one should handle this event.
        // need to be after check that it is for slider to ensure not blocking other events
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const inControl = edgeInControl.current;

        let moveDistance: number,
            moveDistanceReflection: number,
            newPosition: number;

        if (vertical) {
            moveDistance = clientY.current - touch.clientY;

            moveDistanceReflection =
                (moveDistance / trackerVerticalRef.current.offsetHeight) * 100;

            newPosition = Math.round(
                Math.max(
                    0,
                    Math.min(
                        100,
                        (inControl === SLIDER_CONTROLS.VERTICAL1
                            ? lastBottom.current
                            : lastTop.current) + moveDistanceReflection
                    )
                )
            );
        } else {
            moveDistance = IS_RTL
                ? clientX.current - touch.clientX
                : touch.clientX - clientX.current;

            moveDistanceReflection =
                (moveDistance / trackerRef.current.offsetWidth) * 100;

            newPosition = Math.round(
                Math.max(
                    0,
                    Math.min(
                        100,
                        (inControl === SLIDER_CONTROLS.HORIZONTAL1
                            ? lastLeft.current
                            : lastRight.current) + moveDistanceReflection
                    )
                )
            );
        }

        if (inControl == SLIDER_CONTROLS.HORIZONTAL2) {
            if (IS_RTL) {
                updateMinArm(newPosition);
                setMarkerSize(getAbsDiff(newPosition, lastLeft.current));
                updateChanges(newPosition, lastLeft.current);
            } else {
                updateMaxArm(newPosition);
                setMarkerSize(getAbsDiff(newPosition, lastLeft.current));
                updateChanges(lastLeft.current, newPosition);
            }
        } else if (inControl == SLIDER_CONTROLS.HORIZONTAL1) {
            if (IS_RTL) {
                updateMaxArm(newPosition);
                setMarkerSize(getAbsDiff(newPosition, lastRight.current));
                updateChanges(lastRight.current, newPosition);
            } else {
                updateMinArm(newPosition);
                setMarkerSize(getAbsDiff(newPosition, lastRight.current));
                updateChanges(newPosition, lastRight.current);
            }
        } else if (inControl == SLIDER_CONTROLS.VERTICAL2) {
            updateMinArm(newPosition);
            setMarkerSize(getAbsDiff(newPosition, lastBottom.current));
            updateChanges(maxPosition, newPosition);
        } else if (inControl == SLIDER_CONTROLS.VERTICAL1) {
            updateMaxArm(newPosition);
            setMarkerSize(getAbsDiff(newPosition, lastTop.current));
            updateChanges(newPosition, minPosition);
        }
    };
    const handleTouchEnd = () => {
        edgeInControl.current = null;

        lastRight.current = null;
        lastLeft.current = null;

        lastTop.current = null;
        lastBottom.current = null;

        clientX.current = null;
        clientY.current = null;
    };

    return (
        <div
            id={`filter-${slugify(label)}`}
            className={
                'py-3 relative transition-[height] duration-[ease-out] delay-[0.5s]'
            }
            onTouchStart={e => {
                // this disables closing button sheet with drag down.
                e.stopPropagation();
            }}
        >
            <div className={'flex items-center justify-between mb-[22px]'}>
                <Typography
                    className={'text-gray-700'}
                    variant={'captionMedium'}
                >
                    {label}
                </Typography>
            </div>

            <div className={'flex flex-col h-[calc(80vh-200px)] lg:h-fit '}>
                <div
                    className={
                        'hidden lg:flex items-center justify-between h-5 min-h-5'
                    }
                >
                    <Typography className={'text-[1rem] font-medium'}>
                        {formatDisplayedValue(
                            getAbsVal(Math.min(minPosition, maxPosition))
                        )}
                    </Typography>
                    <Typography className={'text-[1rem] font-medium'}>
                        {formatDisplayedValue(
                            getAbsVal(Math.max(minPosition, maxPosition))
                        )}
                    </Typography>
                </div>
                <div
                    className={
                        'flex w-full lg:m-auto items-center h-full min-height-full lg:min-h-auto lg:h-fit justify-center p-2'
                    }
                >
                    <div
                        className={
                            'py-1 relative min-h-full lg:min-h-[auto] min-w-full h-full'
                        }
                    >
                        <div
                            id={'trackerRef'}
                            ref={trackerRef}
                            onPointerUp={handleBarClicked}
                            className={`bg-gray-200 rounded-full min-h-auto h-2 ${
                                vertical ? ' hidden ' : ''
                            }`}
                        >
                            <div
                                className={
                                    'absolute h-2 rounded-full bg-primary-600 w-0'
                                }
                                style={
                                    IS_RTL
                                        ? {
                                              width: `${markerSize}%`,
                                              right: `${Math.min(
                                                  maxPosition,
                                                  minPosition
                                              )}%`,
                                          }
                                        : {
                                              width: `${markerSize}%`,
                                              left: `${Math.min(
                                                  maxPosition,
                                                  minPosition
                                              )}%`,
                                          }
                                }
                            >
                                {' '}
                            </div>
                            <div
                                unselectable="on"
                                onPointerDown={evt =>
                                    handleTouchStart(
                                        evt,
                                        SLIDER_CONTROLS.HORIZONTAL2
                                    )
                                }
                                onTouchStart={evt =>
                                    handleTouchStart(
                                        evt,
                                        SLIDER_CONTROLS.HORIZONTAL2
                                    )
                                }
                                //@ts-ignore
                                ref={rightHand}
                                style={
                                    IS_RTL
                                        ? { right: `${minPosition}%` }
                                        : { left: `${maxPosition}%` }
                                }
                                className={
                                    'absolute ar:-mr-3 en:-ml-3 -top-0.5'
                                }
                            >
                                <button
                                    className={
                                        'hover:shadow-[0px_0px_0px_8px_rgba(0,155,93,0.16)]' +
                                        ' active:shadow-[0px_0px_0px_8px_rgba(0,155,93,0.16)] ' +
                                        ' h-5 flex items-center justify-center w-5 ' +
                                        'rounded-full bg-white shadow border border-solid border-primary-600 ' +
                                        ' cursor-pointer'
                                    }
                                ></button>
                            </div>

                            <div
                                unselectable="on"
                                onPointerDown={evt =>
                                    handleTouchStart(
                                        evt,
                                        SLIDER_CONTROLS.HORIZONTAL1
                                    )
                                }
                                onTouchStart={evt =>
                                    handleTouchStart(
                                        evt,
                                        SLIDER_CONTROLS.HORIZONTAL1
                                    )
                                }
                                ref={leftHand}
                                style={
                                    IS_RTL
                                        ? { right: `${maxPosition}%` }
                                        : { left: `${minPosition}%` }
                                }
                                className={
                                    'absolute ar:-mr-3 en:-ml-3 -top-0.5'
                                }
                            >
                                <button
                                    className={
                                        'hover:shadow-[0px_0px_0px_8px_rgba(0,155,93,0.16)]' +
                                        ' active:shadow-[0px_0px_0px_8px_rgba(0,155,93,0.16)] ' +
                                        ' h-5 flex items-center justify-center w-5 ' +
                                        'rounded-full bg-white shadow border border-solid border-primary-600 ' +
                                        ' cursor-pointer'
                                    }
                                ></button>
                            </div>
                        </div>
                        <div
                            id={'trackerRef-vertical'}
                            ref={trackerVerticalRef}
                            onPointerUp={handleBarClicked}
                            className={` bg-gray-200 rounded-full min-h-full  h-full w-3   mb-7 ${
                                !vertical ? ' hidden' : ''
                            }`}
                        >
                            <div
                                className={
                                    'absolute rounded-full bg-primary-600  w-3   '
                                }
                                style={{
                                    height: `${markerSize}%`,
                                    // height: '40%',
                                    bottom: `${Math.min(
                                        positionBottom,
                                        positionTop
                                    )}%`,
                                }}
                            >
                                {' '}
                            </div>
                            <div
                                onPointerDown={evt =>
                                    handleTouchStart(
                                        evt,
                                        SLIDER_CONTROLS.VERTICAL2
                                    )
                                }
                                onTouchStart={evt =>
                                    handleTouchStart(
                                        evt,
                                        SLIDER_CONTROLS.VERTICAL2
                                    )
                                }
                                ref={leftHand}
                                style={{
                                    bottom: `${positionTop}%`,
                                }}
                                className={
                                    'absolute  p-2 ar:-right-3.5 en:-left-3.5 -mb-5'
                                }
                            >
                                <span
                                    className={'range-slider-vertical-control '}
                                >
                                    <input
                                        className={
                                            'range-slider-vertical-control-input'
                                        }
                                        aria-orientation="vertical"
                                        type="range"
                                        id="VERTICAL2"
                                        value="24"
                                        onChange={() => {
                                            //ignore event
                                            // this input to avoid touch events on safari.
                                        }}
                                        min="1"
                                        max="100"
                                    />

                                    <button
                                        className="range-slider-vertical-control-tool-tip-cont"
                                        role="tooltip"
                                    >
                                        <span className="range-slider-vertical-control-tool-tip-inner"></span>
                                        {formatDisplayedValue(
                                            getAbsVal(positionTop)
                                        )}
                                    </button>
                                </span>
                            </div>

                            <div
                                onPointerDown={evt =>
                                    handleTouchStart(
                                        evt,
                                        SLIDER_CONTROLS.VERTICAL1
                                    )
                                }
                                onTouchStart={evt =>
                                    handleTouchStart(
                                        evt,
                                        SLIDER_CONTROLS.VERTICAL1
                                    )
                                }
                                ref={leftHand}
                                style={{
                                    bottom: `${positionBottom}%`,
                                }}
                                className={
                                    'absolute  p-2 ar:-right-3.5 en:-left-3.5 -mb-5'
                                }
                            >
                                <span
                                    className={'range-slider-vertical-control '}
                                >
                                    <input
                                        className={
                                            'range-slider-vertical-control-input'
                                        }
                                        aria-orientation="vertical"
                                        type="range"
                                        id="VERTICAL1"
                                        value="24"
                                        onChange={() => {
                                            //ignore event
                                            // this input to avoid touch events on safari.
                                        }}
                                        min="1"
                                        max="100"
                                    />

                                    <button
                                        className="range-slider-vertical-control-tool-tip-cont"
                                        role="tooltip"
                                    >
                                        <span className="range-slider-vertical-control-tool-tip-inner"></span>
                                        {formatDisplayedValue(
                                            getAbsVal(positionBottom)
                                        )}
                                    </button>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilterGroupSlider;
